/**
 * The Calendar tab's data: everything with a date the family cares about,
 * a year back and a year ahead, in one sorted list.
 *
 *   - birthdays, wedding anniversaries, yahrzeits and the chagim — from
 *     `occasions.ts`, computed once for the past year and once for the coming
 *     year (each person has exactly one of each per year);
 *   - the family's events and milestones — Moments marked as an event that
 *     carry a date (a bar mitzvah next month, a wedding last spring).
 *
 * Nothing is stored; the list is derived from what the app already knows.
 */
import { upcomingOccasions, type Occasion, type OccasionKind, type OccasionSource } from './occasions';
import { addDays, daysBetween } from './places';
import type { FamilyPhoto, FuzzyDate, UUID } from './types';

export type CalendarKind = OccasionKind | 'event';

export interface CalendarEntry {
  id: string;
  kind: CalendarKind;
  /** YYYY-MM-DD. */
  date: string;
  /** Days from today: negative = past, 0 = today. */
  inDays: number;
  personIds: UUID[];
  /** Set for birthdays / anniversaries / yahrzeits / holidays. */
  occasion?: Occasion;
  /** Set for events and milestones (a Moment marked as an event). */
  moment?: FamilyPhoto;
  /** True when the Moment's date is only known to the month or the year (drawn on its first day). */
  approximate?: boolean;
}

export const CALENDAR_PAST_DAYS = 365;
export const CALENDAR_AHEAD_DAYS = 365;

/** The day a Moment's fuzzy date falls on: the day itself, or the first day of the month / year it names. */
export function momentDay(d: FuzzyDate | undefined): { iso: string; approximate: boolean } | null {
  if (!d || !d.start) return null;
  const m = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(d.start);
  if (!m) return null;
  const exact = d.kind === 'exact' && !!m[3];
  return { iso: `${m[1]}-${m[2] ?? '01'}-${m[3] ?? '01'}`, approximate: !exact };
}

export function buildCalendar(src: OccasionSource, moments: FamilyPhoto[], today: string, pastDays = CALENDAR_PAST_DAYS, aheadDays = CALENDAR_AHEAD_DAYS): CalendarEntry[] {
  const out = new Map<string, CalendarEntry>();
  const add = (e: CalendarEntry) => {
    if (e.inDays < -pastDays || e.inDays > aheadDays) return;
    if (!out.has(e.id)) out.set(e.id, e);
  };

  // Past year: occasions counted from a year ago (the next occurrence after that day), then the coming year from today.
  const fromOccasion = (o: Occasion): CalendarEntry => ({ id: o.id, kind: o.kind, date: o.date, inDays: daysBetween(today, o.date), personIds: o.personIds, occasion: { ...o, inDays: daysBetween(today, o.date) } });
  for (const o of upcomingOccasions(src, addDays(today, -pastDays), pastDays - 1)) add(fromOccasion(o));
  for (const o of upcomingOccasions(src, today, aheadDays)) add(fromOccasion(o));

  for (const m of moments) {
    // A picture or clip the family added to something (a simcha, a story, a birth) belongs to
    // that day, not to a day of its own — see domain/milestones.ts.
    if (!m.isEvent || m.addedTo) continue;
    const day = momentDay(m.takenOn);
    if (!day) continue;
    add({ id: `event:${m.id}`, kind: 'event', date: day.iso, inDays: daysBetween(today, day.iso), personIds: m.personIds, moment: m, approximate: day.approximate });
  }

  return [...out.values()].sort((a, b) => a.inDays - b.inDays || rank(a) - rank(b) || a.id.localeCompare(b.id));
}

/** On one day: the family's own events first, then birthdays, anniversaries, yahrzeits, and the chagim last. */
function rank(e: CalendarEntry): number {
  switch (e.kind) {
    case 'event': return e.moment?.milestone ? 0 : 1;
    case 'birthday': return 2;
    case 'anniversary': return 3;
    case 'yahrzeit': return 4;
    default: return 5;
  }
}

/** YYYY-MM of an entry, for grouping under month headings. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}
